import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "@/lib/format";
import {
    RefreshCw, Rocket, ShieldAlert, ShieldCheck, GitBranch, GitCommit,
    Clock, CheckCircle2, XCircle, Loader2, Terminal, Copy, PlayCircle,
    Undo2, Mail, Heart,
} from "lucide-react";

const STATUS_META_KEYS = {
    queued:   { icon: Clock,        cls: "bg-slate-100 text-slate-700",    key: "queued" },
    running:  { icon: Loader2,      cls: "bg-sky-50 text-sky-700",         key: "running", spin: true },
    success:  { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700", key: "success" },
    failed:   { icon: XCircle,      cls: "bg-rose-50 text-rose-700",       key: "failed" },
    rollback: { icon: Undo2,        cls: "bg-amber-50 text-amber-700",     key: "rollback" },
};

function ago(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return formatDateTime(d);
}

function StatusPill({ status }) {
    const { t } = useTranslation();
    const meta = STATUS_META_KEYS[status] || STATUS_META_KEYS.queued;
    const Icon = meta.icon;
    return (
        <span
            data-testid={`deploy-status-${status}`}
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}
        >
            <Icon className={`h-3.5 w-3.5 ${meta.spin ? "animate-spin" : ""}`} />
            {t(`super_admin.deploy.status.${meta.key}`)}
        </span>
    );
}

export default function SuperDeploy() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [config, setConfig] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [triggering, setTriggering] = useState(false);
    const [reason, setReason] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: list }, { data: cfg }] = await Promise.all([
                api.get("/superadmin/deploys?limit=25"),
                api.get("/superadmin/deploys/config"),
            ]);
            setRows(list);
            setConfig(cfg);
            if (!selectedId && list.length) setSelectedId(list[0].id);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load deploys");
        } finally {
            setLoading(false);
        }
    };

    const loadDetail = async (id) => {
        if (!id) return;
        try {
            const { data } = await api.get(`/superadmin/deploys/${id}`);
            setDetail(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => { loadDetail(selectedId); }, [selectedId]);

    // Poll while a deploy is running / queued so the UI updates live.
    useEffect(() => {
        const isActive = rows.some((r) => r.status === "running" || r.status === "queued");
        if (!isActive) return;
        const t = setInterval(() => { load(); if (selectedId) loadDetail(selectedId); }, 3000);
        return () => clearInterval(t);
    }, [rows, selectedId]);

    const triggerManual = async () => {
        setTriggering(true);
        try {
            const { data } = await api.post("/superadmin/deploys/trigger", {
                branch: "main",
                reason: reason || `manual from super admin`,
            });
            toast.success("Deploy queued");
            setReason("");
            setSelectedId(data.deploy_id);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to trigger");
        } finally {
            setTriggering(false);
        }
    };

    const rollback = async () => {
        if (!detail) return;
        const target = detail.previous_head;
        if (!target || target === "unknown") {
            toast.error("No previous commit recorded for this deploy — cannot rollback.");
            return;
        }
        if (!window.confirm(`Rollback to ${String(target).slice(0, 10)}? This will rebuild containers.`)) return;
        try {
            const { data } = await api.post(`/superadmin/deploys/${detail.id}/rollback`, {});
            toast.success("Rollback queued");
            setSelectedId(data.deploy_id);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rollback failed");
        }
    };

    const webhookUrl = useMemo(() => {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        return `${base.replace(/\/$/, "")}/api/webhooks/github-deploy`;
    }, []);

    const copy = (text, label) => {
        navigator.clipboard?.writeText(text).then(
            () => toast.success(`${label} copied`),
            () => toast.error("Copy failed"),
        );
    };

    return (
        <>
            <Topbar
                title={t("super_admin.deploy_title")}
                subtitle={t("super_admin.deploy_subtitle")}
            />

            <div className="p-6 space-y-6" data-testid="super-deploy-page">
                {/* Config strip */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="rounded-xl border bg-white p-4 lg:col-span-2">
                        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Webhook URL</div>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-slate-50 rounded px-2 py-1.5 border truncate" data-testid="webhook-url">
                                {webhookUrl}
                            </code>
                            <button
                                data-testid="copy-webhook-url"
                                onClick={() => copy(webhookUrl, "URL")}
                                className="p-1.5 rounded hover:bg-slate-100"
                                title="Copy"
                            >
                                <Copy className="h-4 w-4 text-slate-600" />
                            </button>
                        </div>
                        <div className="mt-2 text-xs text-slate-500">
                            Content type: <code>application/json</code> · Event: <code>push</code>
                        </div>
                    </div>

                    <div className="rounded-xl border bg-white p-4">
                        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Security</div>
                        <div className="flex items-center gap-2 text-sm">
                            {config?.webhook_configured ? (
                                <>
                                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                                    <span className="text-emerald-700 font-medium">HMAC configured</span>
                                </>
                            ) : (
                                <>
                                    <ShieldAlert className="h-4 w-4 text-amber-600" />
                                    <span className="text-amber-700 font-medium">Set <code>GITHUB_WEBHOOK_SECRET</code></span>
                                </>
                            )}
                        </div>
                        <div className="mt-2 text-xs text-slate-500">
                            Branches: {config?.branch_allowlist?.length ? config.branch_allowlist.join(", ") : "all"}
                        </div>
                    </div>

                    <div className="rounded-xl border bg-white p-4">
                        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Deploy script</div>
                        <div className="flex items-center gap-2 text-sm">
                            {config?.script_exists && config?.script_executable ? (
                                <>
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                    <span className="text-emerald-700 font-medium">Ready</span>
                                </>
                            ) : (
                                <>
                                    <XCircle className="h-4 w-4 text-rose-600" />
                                    <span className="text-rose-700 font-medium">Missing / not executable</span>
                                </>
                            )}
                        </div>
                        <div className="mt-2 text-xs text-slate-500 truncate" title={config?.script_path}>
                            {config?.script_path || "—"}
                        </div>
                    </div>

                    <div className="rounded-xl border bg-white p-4">
                        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" /> Notifications
                        </div>
                        {config?.notify_recipients?.length ? (
                            <>
                                <div className="text-sm font-medium text-slate-800 truncate" title={config.notify_recipients.join(", ")}>
                                    {config.notify_recipients.join(", ")}
                                </div>
                                <div className="mt-1 text-xs text-emerald-600">Email on every deploy</div>
                            </>
                        ) : (
                            <>
                                <div className="text-sm text-amber-700 font-medium">Off</div>
                                <div className="mt-1 text-xs text-slate-500">Set <code>DEPLOY_NOTIFY_EMAIL</code></div>
                            </>
                        )}
                    </div>

                    <div className="rounded-xl border bg-white p-4 md:col-span-2 lg:col-span-1">
                        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1">
                            <Heart className="h-3.5 w-3.5" /> Health check
                        </div>
                        <div className="text-sm font-medium text-slate-800 truncate" title={config?.health_url}>
                            {config?.health_url || "—"}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                            Timeout {config?.health_timeout ?? 30}s · auto-rollback on fail
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="rounded-xl border bg-white p-4 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-800">Manual deploy</div>
                        <div className="text-xs text-slate-500">
                            Runs <code>git pull &amp;&amp; docker compose build &amp;&amp; docker compose up -d</code> on the VPS.
                        </div>
                    </div>
                    <input
                        data-testid="deploy-reason-input"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason (optional)"
                        className="w-full md:w-72 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff7b02]/30"
                    />
                    <button
                        data-testid="trigger-deploy-btn"
                        onClick={triggerManual}
                        disabled={triggering}
                        className="inline-flex items-center gap-2 bg-[#ff7b02] hover:bg-[#e56d00] text-white font-medium px-4 py-2 rounded-lg disabled:opacity-60"
                    >
                        {triggering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                        Deploy now
                    </button>
                    <button
                        data-testid="refresh-deploys-btn"
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-2 bg-white border hover:bg-slate-50 text-slate-700 font-medium px-3 py-2 rounded-lg"
                        title="Refresh"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>

                {/* List + detail */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1 rounded-xl border bg-white overflow-hidden">
                        <div className="px-4 py-3 border-b text-sm font-semibold text-slate-800">
                            Recent deploys
                        </div>
                        <div className="divide-y max-h-[520px] overflow-y-auto" data-testid="deploys-list">
                            {rows.length === 0 && (
                                <div className="p-8 text-center text-sm text-slate-500">
                                    No deploys yet. Push to your repo or click <b>Deploy now</b>.
                                </div>
                            )}
                            {rows.map((r) => (
                                <button
                                    key={r.id}
                                    data-testid={`deploy-row-${r.id}`}
                                    onClick={() => setSelectedId(r.id)}
                                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${
                                        selectedId === r.id ? "bg-orange-50/40" : ""
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <StatusPill status={r.status} />
                                        <span className="text-xs text-slate-500">{ago(r.created_at)}</span>
                                    </div>
                                    <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
                                        <GitBranch className="h-3.5 w-3.5" />
                                        <span className="font-medium">{r.branch || "—"}</span>
                                        {r.commit && (
                                            <>
                                                <GitCommit className="h-3.5 w-3.5 ml-1" />
                                                <code className="text-[11px]">{String(r.commit).slice(0, 7)}</code>
                                            </>
                                        )}
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-slate-500">
                                        {r.source === "github" ? (
                                            <>via GitHub · {r.pusher || "unknown"}</>
                                        ) : (
                                            <>manual · {r.pusher || "—"}</>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-2 rounded-xl border bg-white overflow-hidden">
                        <div className="px-4 py-3 border-b flex items-center gap-2">
                            <Terminal className="h-4 w-4 text-slate-500" />
                            <div className="text-sm font-semibold text-slate-800">
                                {detail ? `Deploy ${detail.id.slice(0, 8)}` : "Select a deploy"}
                            </div>
                            {detail && <span className="ml-2"><StatusPill status={detail.status} /></span>}
                            {detail && detail.previous_head && detail.previous_head !== "unknown" && (
                                <button
                                    data-testid="rollback-btn"
                                    onClick={rollback}
                                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-medium"
                                    title={`Rollback to ${String(detail.previous_head).slice(0, 10)}`}
                                >
                                    <Undo2 className="h-3.5 w-3.5" />
                                    Rollback
                                </button>
                            )}
                        </div>
                        {!detail && (
                            <div className="p-10 text-center text-sm text-slate-500">
                                <PlayCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                                Pick a deploy on the left to see its output.
                            </div>
                        )}
                        {detail && (
                            <div className="p-4 space-y-3">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                    <div>
                                        <div className="text-slate-500">Branch</div>
                                        <div className="font-medium text-slate-800">{detail.branch || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-slate-500">Commit</div>
                                        <code className="font-medium text-slate-800">
                                            {detail.commit ? String(detail.commit).slice(0, 10) : "—"}
                                        </code>
                                    </div>
                                    <div>
                                        <div className="text-slate-500">Duration</div>
                                        <div className="font-medium text-slate-800">
                                            {detail.duration_ms != null ? `${(detail.duration_ms / 1000).toFixed(1)}s` : "—"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-slate-500">Exit code</div>
                                        <div className="font-medium text-slate-800">
                                            {detail.exit_code == null ? "—" : detail.exit_code}
                                        </div>
                                    </div>
                                </div>
                                <pre
                                    data-testid="deploy-output"
                                    className="bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] leading-relaxed max-h-[440px] overflow-auto whitespace-pre-wrap"
                                >
{detail.output_tail || "(waiting for output…)"}
                                </pre>
                            </div>
                        )}
                    </div>
                </div>

                {/* GitHub setup instructions */}
                <div className="rounded-xl border bg-white p-5" data-testid="github-instructions">
                    <div className="text-sm font-semibold text-slate-800 mb-3">
                        Configure the GitHub webhook (one-time)
                    </div>
                    <ol className="list-decimal ml-5 space-y-2 text-sm text-slate-700">
                        <li>
                            On your VPS, ensure the deploy script exists at{" "}
                            <code>{config?.script_path || "/opt/akomoda/deploy/auto_deploy.sh"}</code>{" "}
                            and is executable (<code>chmod +x</code>).
                        </li>
                        <li>
                            Add these env vars to your backend container (<code>backend.env</code>) and restart it:
                            <pre className="mt-1 bg-slate-50 border rounded p-2 text-xs">
{`GITHUB_WEBHOOK_SECRET=<long random string>
DEPLOY_SCRIPT=/opt/akomoda/deploy/auto_deploy.sh
DEPLOY_BRANCHES=            # empty = accept all branches`}
                            </pre>
                        </li>
                        <li>
                            In GitHub → <b>Settings → Webhooks → Add webhook</b>:
                            <ul className="list-disc ml-5 mt-1 space-y-0.5 text-slate-600">
                                <li>Payload URL: <code>{webhookUrl}</code></li>
                                <li>Content type: <code>application/json</code></li>
                                <li>Secret: the value of <code>GITHUB_WEBHOOK_SECRET</code></li>
                                <li>Event: <b>Just the push event</b></li>
                            </ul>
                        </li>
                        <li>
                            Push any commit — the run will appear here and the containers will rebuild.
                        </li>
                    </ol>
                </div>
            </div>
        </>
    );
}
